import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Partnership } from "../Entities/Partnership";
import { usePartnerships } from "../hooks/usePartnerships";
import { AuthService } from "../Entities/AuthService";
import { Button } from "@/Components/ui/button";
import { Input } from "@/Components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/Components/ui/select";
import { Card, CardHeader, CardContent } from "@/Components/ui/card";
import { Plus, Search, Filter, Info, FileText, ChevronLeft, ChevronRight, ArrowUp, CheckCircle2, XCircle, AlertCircle, Download, ChevronDown, HelpCircle, BookOpen, Upload, MousePointerClick, Eye, Edit, Trash2 } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { parseISO, isValid, differenceInDays } from "date-fns";
import { useToast } from "@/Components/ui/use-toast";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/Components/ui/accordion.jsx";

import PartnershipForm from "@/Components/partnerships/PartnershipForm";
import PartnershipsList from "@/Components/partnerships/PartnershipsList";
import DeleteConfirmationModal from "@/Components/partnerships/DeleteConfirmationModal";
import { Alert, AlertDescription, AlertTitle } from "@/Components/ui/alert";

// Status é calculado automaticamente no backend, não precisa calcular no frontend

export default function Partnerships() {
  const { toast } = useToast();
  const isObserver = AuthService.isObserver(); // ✅ Verificar se é observador
  const [filteredPartnerships, setFilteredPartnerships] = useState([]);
  const isFirstFilterLoad = useRef(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPartnership, setEditingPartnership] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todos os Status');
  const [yearFilter, setYearFilter] = useState('Todos os Anos');
  const [typeFilter, setTypeFilter] = useState('Todos os Tipos');
  const [origemFilter, setOrigemFilter] = useState('Todos as Origens'); 
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partnershipToDelete, setPartnershipToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [origemOptions, setOrigemOptions] = useState([
    { label: 'Todos as Origens', value: 'Todos as Origens', dbValue: 'all' }
  ]);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  const [typeOptions, setTypeOptions] = useState([
    { label: 'Todos os Tipos', value: 'Todos os Tipos', dbValue: 'all' }
  ]);

  // Mapeamento bidirecional: Português ↔ Valores técnicos do banco
  const statusOptions = [
    { label: 'Todos os Status', value: 'Todos os Status', dbValue: 'all' },
    { label: 'No Prazo', value: 'No Prazo', dbValue: 'no_prazo' },
    { label: 'Quitado', value: 'Quitado', dbValue: 'quitado' },
    { label: 'Em Débito', value: 'Em Débito', dbValue: 'em_debito' },
    { label: 'Verificar', value: 'Verificar', dbValue: 'verificar' }
  ];

  const [yearOptions, setYearOptions] = useState([
    { label: 'Todos os Anos', value: 'Todos os Anos', dbValue: 'all' }
  ]);

  // Funções para converter entre português e valores técnicos (MOVER PARA CIMA)
  const getDbValueFromLabel = (label, options) => {
    const option = options.find(opt => opt.value === label);
    return option ? option.dbValue : label;
  };

  const getLabelFromDbValue = (dbValue, options) => {
    const option = options.find(opt => opt.dbValue === dbValue);
    return option ? option.label : dbValue;
  };

  // Preparar filtros para React Query (AGORA PODE USAR AS FUNÇÕES)
  const queryFilters = {
    status: statusFilter !== 'Todos os Status' ? getDbValueFromLabel(statusFilter, statusOptions) : undefined,
    year: yearFilter !== 'Todos os Anos' ? yearFilter : undefined,
    tipo_parceria: typeFilter !== 'Todos os Tipos' ? getDbValueFromLabel(typeFilter, typeOptions) : undefined,
    origem: origemFilter !== 'Todos as Origens' ? getDbValueFromLabel(origemFilter, origemOptions) : undefined,
    search: searchTerm || undefined,
  };

  // Usar hook customizado com React Query
  const {
    data: partnerships,
    total: totalItems,
    totalPages,
    isLoading,
    isFetching,
    prefetchNextPage,
    prefetchPreviousPage,
    invalidateCache,
  } = usePartnerships(queryFilters, currentPage, itemsPerPage);

  // Filtros agora são gerenciados pelo React Query via queryFilters

  // Função para carregar origens únicas (extraída para reutilização)
  const loadOrigens = useCallback(async () => {
    try {
      const data = await Partnership.listAll();
      // Extrair origens únicas, excluindo "Não Informado"
      const uniqueOrigens = [...new Set(
        data
          .map(p => p.origem)
          .filter(o => o && o !== 'Não Informado')
      )].sort();
      
      // Criar opções para o filtro
      const options = [
        { label: 'Todos as Origens', value: 'Todos as Origens', dbValue: 'all' },
        ...uniqueOrigens.map(origem => ({
          label: origem,
          value: origem,
          dbValue: origem
        }))
      ];
      
      setOrigemOptions(options);
    } catch (error) {
      console.error('Erro ao carregar origens:', error);
    }
  }, []);

  // Carregar origens únicas do banco
  useEffect(() => {
    loadOrigens();
  }, [loadOrigens]);

  // Função para carregar tipos únicos (extraída para reutilização)
  const loadTipos = useCallback(async () => {
    try {
      const data = await Partnership.listAll();
      // Extrair tipos únicos que realmente existem nas parcerias
      const uniqueTipos = [...new Set(
        data
          .map(p => p.tipo_parceria)
          .filter(t => t && t.trim() !== '')
      )].sort();
      
      // Se não houver tipos, mostrar apenas "Todos os Tipos"
      if (uniqueTipos.length === 0) {
        setTypeOptions([
          { label: 'Todos os Tipos', value: 'Todos os Tipos', dbValue: 'all' }
        ]);
      } else {
        // Criar opções para o filtro com os tipos reais do banco
        const options = [
          { label: 'Todos os Tipos', value: 'Todos os Tipos', dbValue: 'all' },
          ...uniqueTipos.map(tipo => ({
            label: tipo,
            value: tipo,
            dbValue: tipo
          }))
        ];
        
        setTypeOptions(options);
      }
    } catch (error) {
      console.error('Erro ao carregar tipos:', error);
    }
  }, []);

  // Carregar tipos únicos do banco (dinâmico)
  useEffect(() => {
    loadTipos();
  }, [loadTipos]);

  // Função para carregar anos únicos
  const loadYears = useCallback(async () => {
    try {
      const data = await Partnership.listAll();
      // Extrair anos únicos
      const uniqueYears = [...new Set(
        data
          .map(p => p.ano)
          .filter(ano => ano && !isNaN(ano))
      )].sort((a, b) => b - a); // Ordenar do mais recente para o mais antigo
      
      // Criar opções para o filtro
      const options = [
        { label: 'Todos os Anos', value: 'Todos os Anos', dbValue: 'all' },
        ...uniqueYears.map(ano => ({
          label: ano.toString(),
          value: ano.toString(),
          dbValue: ano.toString()
        }))
      ];
      
      setYearOptions(options);
    } catch (error) {
      console.error('Erro ao carregar anos:', error);
    }
  }, []);

  // Carregar anos únicos do banco
  useEffect(() => {
    loadYears();
  }, [loadYears]);

  // Prefetch automático da próxima página
  useEffect(() => {
    if (!isLoading && partnerships && partnerships.length > 0) {
      prefetchNextPage();
    }
  }, [currentPage, isLoading, partnerships, prefetchNextPage]);

  // ✅ Socket.IO removido - atualização automática via React Query global
  // Configuração global em main.jsx + useGlobalSocket.js fazem o trabalho
  // Quando Socket.IO emite 'data_updated', React Query invalida cache automaticamente
  // 
  // Benefícios:
  // - Menos código duplicado
  // - Atualização automática em TODAS as páginas
  // - Performance melhorada (1 conexão Socket.IO ao invés de N)
  // - Manutenção centralizada

  // Reset para página 1 quando filtros mudarem
  useEffect(() => {
    if (!isFirstFilterLoad.current) {
      setCurrentPage(1);
    } else {
      isFirstFilterLoad.current = false;
    }
  }, [statusFilter, yearFilter, typeFilter, origemFilter, searchTerm]);

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      // Status é calculado automaticamente no backend
      if (editingPartnership) {
        await Partnership.update(editingPartnership.id, formData);
      } else {
        await Partnership.create(formData);
      }
      // Invalidar cache do React Query
      invalidateCache();
      
      // Recarregar filtros para incluir novos tipos/origens
      await Promise.all([loadTipos(), loadOrigens()]);
      
      setShowForm(false);
      setEditingPartnership(null);
      
      // Notificação de sucesso
      toast({
        title: editingPartnership ? "Parceria atualizada!" : "Parceria criada!",
        description: editingPartnership 
          ? "As alterações foram salvas com sucesso."
          : "A nova parceria foi cadastrada com sucesso.",
        className: "bg-green-50 border-green-200",
      });
    } catch (error) {
      console.error('Erro ao salvar parceria:', error);
      toast({
        variant: "destructive",
        title: "Erro ao salvar parceria",
        description: error.message || "Não foi possível salvar a parceria. Verifique os dados e tente novamente.",
      });
    }
    setIsSaving(false);
  };

  const handleEdit = (partnership) => {
    setEditingPartnership(partnership);
    setShowForm(true);
  };

  const handleDelete = (partnership) => {
    setPartnershipToDelete(partnership);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!partnershipToDelete) return;

    setIsDeleting(true);
    try {
      await Partnership.delete(partnershipToDelete.id);
      setShowDeleteModal(false);
      setPartnershipToDelete(null);
      
      // Invalidar cache do React Query
      invalidateCache();
      
      // Notificação de sucesso
      toast({
        title: "Parceria excluída!",
        description: "A parceria foi removida com sucesso do sistema.",
        className: "bg-green-50 border-green-200",
      });
    } catch (error) {
      console.error('Erro ao excluir parceria:', error);
      toast({
        variant: "destructive",
        title: "Erro ao excluir parceria",
        description: "Não foi possível excluir a parceria. Tente novamente.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPartnershipToDelete(null);
  };

  // Função de exportação
  const handleExport = async (format) => {
    setIsExporting(true);
    setShowExportMenu(false);

    try {
      const filters = {
        search: searchTerm,
        status: statusFilter !== 'Todos os Status' ? getDbValueFromLabel(statusFilter, statusOptions) : undefined,
        year: yearFilter !== 'Todos os Anos' ? yearFilter : undefined,
        tipo: typeFilter !== 'Todos os Tipos' ? getDbValueFromLabel(typeFilter, typeOptions) : undefined,
        origem: origemFilter !== 'Todos as Origens' ? getDbValueFromLabel(origemFilter, origemOptions) : undefined,
      };

      await Partnership.export(format, filters);

      toast({
        title: "Exportação concluída!",
        description: `Arquivo ${format.toUpperCase()} baixado com sucesso.`,
        className: "bg-green-50 border-green-200",
      });
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast({
        variant: "destructive",
        title: "Erro na exportação",
        description: "Não foi possível exportar os dados. Tente novamente.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  const handleCancel = () => {
    setShowForm(false);
    setEditingPartnership(null);
  };



  // Funções de paginação server-side
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const goToPage = (page) => {
    const newPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(newPage);
    scrollToTop();
    
    // Prefetch páginas adjacentes
    setTimeout(() => {
      prefetchNextPage();
      prefetchPreviousPage();
    }, 100);
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalOSCs = totalItems;

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Parcerias</h1>
            <p className="text-slate-600 mt-1">Gerencie todas as parcerias e contratos com OSCs</p>
          </div>
          <div className="flex gap-2">
            {/* Botão de Exportação - Oculto para observadores */}
            {!isObserver && (
              <div className="relative" ref={exportMenuRef}>
                <Button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={isExporting}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      Exportando...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span className="hidden sm:inline">Exportar</span>
                      <ChevronDown className="w-3 h-3" />
                    </>
                  )}
                </Button>
                
                {/* Dropdown Menu */}
                {showExportMenu && !isExporting && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 z-50">
                    <div className="py-1">
                      <button
                        onClick={() => handleExport('excel')}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4 text-green-600" />
                        Excel (.xlsx)
                      </button>
                      <button
                        onClick={() => handleExport('csv')}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4 text-blue-600" />
                        CSV (.csv)
                      </button>
                      <button
                        onClick={() => handleExport('pdf')}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4 text-red-600" />
                        PDF (.pdf)
                      </button>
                      <button
                        onClick={() => handleExport('txt')}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4 text-slate-600" />
                        Texto (.txt)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isObserver && ( // ⛔ Ocultar para observadores
              <Button
                onClick={() => setShowForm(true)}
                className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Nova Parceria</span>
                <Plus className="w-4 h-4 sm:hidden" />
              </Button>
            )}
          </div>
        </div>

        {/* Accordion de Ajuda */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-0">
            <Accordion type="single" collapsible className="w-full">
              {/* Accordion Principal - Central de Ajuda */}
              <AccordionItem value="help-center" className="border-none">
                <AccordionTrigger className="hover:no-underline px-4 py-4">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-blue-600" />
                    <h3 className="font-bold text-blue-900 text-lg">Central de Ajuda</h3>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <Accordion type="single" collapsible className="w-full space-y-2">
              {/* Tópico 1: Como Adicionar Parcerias */}
              <AccordionItem value="item-1" className="bg-white rounded-lg border border-blue-200 px-4">
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center gap-2 text-left">
                    <Plus className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="font-semibold text-blue-900">Como Adicionar Parcerias</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-blue-800 pb-4">
                  <div className="space-y-3 pl-6">
                    <p className="text-sm">Você pode adicionar parcerias de duas formas:</p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 bg-blue-50 p-3 rounded">
                        <MousePointerClick className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-sm">Manualmente</p>
                          <p className="text-xs text-blue-700 mt-1">Clique em "Nova Parceria" para preencher o formulário de cadastro com todos os dados.</p>
                        </div>
                      </div>
                      <div className="bg-blue-50 p-3 rounded border border-blue-200">
                        <div className="flex items-start gap-2">
                          <Upload className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold text-sm">Em Lote (Importação)</p>
                            <p className="text-xs text-blue-700 mt-1">Vá para a página "Importar Dados" para fazer upload de arquivos Excel com múltiplas parcerias de uma só vez.</p>
                            <Link to="/import">
                              <Button 
                                size="sm" 
                                className="mt-3 bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                              >
                                <Upload className="w-3 h-3" />
                                Ir para Importação
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Tópico 2: Como Usar os Filtros */}
              <AccordionItem value="item-2" className="bg-white rounded-lg border border-blue-200 px-4">
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center gap-2 text-left">
                    <Filter className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="font-semibold text-blue-900">Como Usar os Filtros</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-blue-800 pb-4">
                  <div className="space-y-3 pl-6">
                    <p className="text-sm">Use os filtros para encontrar parcerias específicas:</p>
                    <ul className="space-y-2 text-xs">
                      <li className="flex items-start gap-2">
                        <Search className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Busca:</strong> Digite OSC, CNPJ ou número do processo</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Status:</strong> Filtre por No Prazo, Quitado, Em Débito ou Verificar</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Origem:</strong> Filtre por fonte de recurso (Federal, Estadual, Municipal, etc.)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <BookOpen className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Ano:</strong> Selecione o ano da parceria</span>
                      </li>
                    </ul>
                    <p className="text-xs bg-blue-50 p-2 rounded mt-2">
                      💡 <strong>Dica:</strong> Combine múltiplos filtros para resultados mais precisos!
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Tópico 3: Ações Disponíveis */}
              <AccordionItem value="item-3" className="bg-white rounded-lg border border-blue-200 px-4">
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center gap-2 text-left">
                    <MousePointerClick className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="font-semibold text-blue-900">Ações Disponíveis</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-blue-800 pb-4">
                  <div className="space-y-3 pl-6">
                    <p className="text-sm">Para cada parceria, você pode:</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div className="flex items-start gap-2 bg-green-50 p-3 rounded border border-green-200">
                        <Eye className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-sm text-green-900">Visualizar</p>
                          <p className="text-xs text-green-700 mt-1">Ver todos os detalhes da parceria</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 bg-blue-50 p-3 rounded border border-blue-200">
                        <Edit className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-sm text-blue-900">Editar</p>
                          <p className="text-xs text-blue-700 mt-1">Modificar informações da parceria</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 bg-red-50 p-3 rounded border border-red-200">
                        <Trash2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-sm text-red-900">Excluir</p>
                          <p className="text-xs text-red-700 mt-1">Remover parceria do sistema</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs bg-amber-50 p-2 rounded mt-2 border border-amber-200">
                      ⚠️ <strong>Observadores:</strong> Usuários com perfil "Observador" podem apenas visualizar, sem permissão para editar ou excluir.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Tópico 4: Exportar Dados */}
              <AccordionItem value="item-4" className="bg-white rounded-lg border border-blue-200 px-4">
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center gap-2 text-left">
                    <Download className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="font-semibold text-blue-900">Exportar Dados</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-blue-800 pb-4">
                  <div className="space-y-3 pl-6">
                    <p className="text-sm">Exporte os dados filtrados em diversos formatos:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="bg-green-50 p-2 rounded border border-green-200 text-center">
                        <FileText className="w-5 h-5 text-green-600 mx-auto mb-1" />
                        <p className="text-xs font-semibold text-green-900">Excel</p>
                        <p className="text-[10px] text-green-700">.xlsx</p>
                      </div>
                      <div className="bg-blue-50 p-2 rounded border border-blue-200 text-center">
                        <FileText className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                        <p className="text-xs font-semibold text-blue-900">CSV</p>
                        <p className="text-[10px] text-blue-700">.csv</p>
                      </div>
                      <div className="bg-red-50 p-2 rounded border border-red-200 text-center">
                        <FileText className="w-5 h-5 text-red-600 mx-auto mb-1" />
                        <p className="text-xs font-semibold text-red-900">PDF</p>
                        <p className="text-[10px] text-red-700">.pdf</p>
                      </div>
                      <div className="bg-slate-50 p-2 rounded border border-slate-200 text-center">
                        <FileText className="w-5 h-5 text-slate-600 mx-auto mb-1" />
                        <p className="text-xs font-semibold text-slate-900">Texto</p>
                        <p className="text-[10px] text-slate-700">.txt</p>
                      </div>
                    </div>
                    <p className="text-xs bg-blue-50 p-2 rounded mt-2">
                      💡 <strong>Dica:</strong> A exportação respeita os filtros ativos. Filtre antes de exportar para obter apenas os dados desejados!
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Tópico 5: Entendendo os Status */}
              <AccordionItem value="item-5" className="bg-white rounded-lg border border-blue-200 px-4">
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center gap-2 text-left">
                    <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="font-semibold text-blue-900">Entendendo os Status</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-blue-800 pb-4">
                  <div className="space-y-2 pl-6">
                    <p className="text-sm mb-3">Cada parceria possui um status calculado automaticamente baseado em regras específicas:</p>
                    <div className="space-y-3">
                      {/* Status: No Prazo */}
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-4 h-4 bg-green-500 rounded-full mt-1 flex-shrink-0"></div>
                          <div className="flex-1">
                            <p className="text-base font-bold text-green-900">No Prazo</p>
                            <p className="text-sm text-green-700 mt-1">Prestação de contas dentro do prazo estabelecido</p>
                          </div>
                        </div>
                        <div className="ml-7 mt-3 p-3 bg-white rounded border border-green-100">
                          <p className="text-xs font-semibold text-green-800 mb-2">📋 Regras de Acionamento:</p>
                          <ul className="text-xs text-green-700 space-y-1 list-disc list-inside">
                            <li>Data de fim da parceria ainda não foi atingida, OU</li>
                            <li>Prazo de prestação de contas (data fim + prazo) ainda não venceu</li>
                            <li>Campo "Análises e Observações" NÃO contém palavras-chave de quitação</li>
                            <li><strong>Cálculo automático</strong> - baseado em datas e prazos</li>
                          </ul>
                        </div>
                      </div>

                      {/* Status: Quitado */}
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-4 h-4 bg-blue-500 rounded-full mt-1 flex-shrink-0"></div>
                          <div className="flex-1">
                            <p className="text-base font-bold text-blue-900">Quitado</p>
                            <p className="text-sm text-blue-700 mt-1">Prestação de contas aprovada e finalizada</p>
                          </div>
                        </div>
                        <div className="ml-7 mt-3 p-3 bg-white rounded border border-blue-100">
                          <p className="text-xs font-semibold text-blue-800 mb-2">📋 Regras de Acionamento:</p>
                          <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                            <li>Campo <strong>"Análises e Observações"</strong> contém as palavras-chave:</li>
                            <li className="ml-4">• "quitado", "quitada", "aprovado", "aprovada"</li>
                            <li className="ml-4">• "homologado", "homologada", "finalizado", "finalizada"</li>
                            <li><strong>Detecção automática</strong> - sistema identifica as palavras-chave</li>
                            <li>Sobrescreve o cálculo automático de status baseado em datas</li>
                          </ul>
                        </div>
                      </div>

                      {/* Status: Em Débito */}
                      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-4 h-4 bg-red-500 rounded-full mt-1 flex-shrink-0"></div>
                          <div className="flex-1">
                            <p className="text-base font-bold text-red-900">Em Débito</p>
                            <p className="text-sm text-red-700 mt-1">Prazo vencido, pendente de prestação de contas</p>
                          </div>
                        </div>
                        <div className="ml-7 mt-3 p-3 bg-white rounded border border-red-100">
                          <p className="text-xs font-semibold text-red-800 mb-2">📋 Regras de Acionamento:</p>
                          <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
                            <li>Data de fim da parceria já passou, E</li>
                            <li>Prazo de prestação de contas (data fim + prazo) venceu</li>
                            <li>Campo "Análises e Observações" NÃO contém palavras-chave de quitação</li>
                            <li><strong>Cálculo automático</strong> - baseado em datas e prazos</li>
                          </ul>
                        </div>
                      </div>

                      {/* Status: Verificar */}
                      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-4 h-4 bg-amber-500 rounded-full mt-1 flex-shrink-0"></div>
                          <div className="flex-1">
                            <p className="text-base font-bold text-amber-900">Verificar</p>
                            <p className="text-sm text-amber-700 mt-1">Requer análise manual ou dados incompletos</p>
                          </div>
                        </div>
                        <div className="ml-7 mt-3 p-3 bg-white rounded border border-amber-100">
                          <p className="text-xs font-semibold text-amber-800 mb-2">📋 Regras de Acionamento:</p>
                          <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
                            <li><strong>Opção 1:</strong> Campo "Análises e Observações" contém palavras-chave:</li>
                            <li className="ml-4">• "verificar", "pendente", "análise", "revisar"</li>
                            <li className="ml-4">• "incompleto", "aguardando", "conferir"</li>
                            <li><strong>Opção 2:</strong> Campos obrigatórios ausentes ou vazios:</li>
                            <li className="ml-4">• Data de fim não informada</li>
                            <li className="ml-4">• Prazo de prestação de contas não informado</li>
                            <li><strong>Detecção automática</strong> - sistema identifica palavras-chave ou dados faltantes</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>


        {/* Filters */}
        <Card>
          <CardHeader>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtros e Busca
            </h3>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4"> {/* Changed to md:grid-cols-5 */}
              <div className="relative md:col-span-2"> {/* Adjusted col-span */}
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar parcerias..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder='Selecione o Status' />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={origemFilter} onValueChange={setOrigemFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={origemFilter || 'Selecione a Origem'} />
                </SelectTrigger>
                <SelectContent>
                  {origemOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={yearFilter || 'Selecione o Ano'} />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={typeFilter || 'Selecione o Tipo'} />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600 pt-2">
                <FileText className="w-4 h-4" />
                Total: {totalItems} parcerias
            </div>
          </CardContent>
        </Card>

        {/* Partnership List */}
        <div className="relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="text-sm font-medium text-slate-700">
                  {partnerships && partnerships.length > 0 ? `Carregando página ${currentPage}...` : 'Carregando...'}
                </p>
              </div>
            </div>
          )}
          <PartnershipsList 
            partnerships={partnerships}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isLoading={isLoading}
          />
        </div>

        {/* Paginação - Estilo OSCReport */}
        {!isLoading && totalPages > 1 && (
          <div className="flex flex-col items-center gap-4 mt-8">
            {/* Controles de Paginação Centralizados */}
            <div className="flex items-center gap-2">
              {/* Botão Anterior */}
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevious}
                disabled={currentPage === 1}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Anterior</span>
              </Button>

              {/* Números das páginas */}
              <div className="flex items-center gap-1">
                {/* Primeira página */}
                {currentPage > 3 && (
                  <>
                    <Button
                      variant={1 === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(1)}
                      className="w-10 h-10"
                    >
                      1
                    </Button>
                    {currentPage > 4 && (
                      <span className="px-2 text-slate-400">...</span>
                    )}
                  </>
                )}

                {/* Páginas próximas à atual */}
                {Array.from({ length: totalPages || 1 }, (_, i) => i + 1)
                  .filter(page => {
                    return page >= currentPage - 2 && page <= currentPage + 2;
                  })
                  .map(page => (
                    <Button
                      key={page}
                      variant={page === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(page)}
                      className="w-10 h-10"
                    >
                      {page}
                    </Button>
                  ))}

                {/* Última página */}
                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && (
                      <span className="px-2 text-slate-400">...</span>
                    )}
                    <Button
                      variant={totalPages === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(totalPages)}
                      className="w-10 h-10"
                    >
                      {totalPages}
                    </Button>
                  </>
                )}
              </div>

              {/* Botão Próximo */}
              <Button
                variant="outline"
                size="sm"
                onClick={goToNext}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1"
              >
                <span className="hidden sm:inline">Próximo</span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Descrição embaixo da paginação */}
            <div className="text-sm text-slate-600 text-center">
              Mostrando {startIndex + 1} a {Math.min(endIndex, totalOSCs || 0)} de {totalOSCs || 0} parcerias
            </div>
          </div>
        )}

        {/* Form Modal */}
        <AnimatePresence>
          {showForm && (
            <PartnershipForm
              partnership={editingPartnership}
              onSave={handleSave}
              onCancel={handleCancel}
              isLoading={isSaving}
            />
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          partnership={partnershipToDelete}
          isDeleting={isDeleting}
        />

        {/* Botão Voltar ao Topo - Estilo OSCReport */}
        {partnerships && partnerships.length > 0 && (
          <div className="flex justify-end mt-8">
            <Button
              onClick={scrollToTop}
              variant="outline"
              size="lg"
              className="flex items-center gap-2 animate-pulse hover:animate-none transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl bg-white/80 backdrop-blur-sm border-2 border-blue-200 hover:border-blue-400 text-blue-700 hover:text-blue-800"
            >
              <ArrowUp className="w-5 h-5" />
              Voltar ao Topo
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
